import React, { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Check, Close, Delete, Edit, UploadFile } from '@mui/icons-material'
import API from '../../../../services/API'
import {
  GPX_ROUTE_COLORS,
  MAX_GPX_POINTS,
  MAX_GPX_ROUTES,
  formatRouteDistance,
  parseGpx,
  routeDistanceMeters,
  simplifyRoute,
} from './gpx'

// Commander dialog for the courses shown on the GPX map view: upload a .gpx
// file (parsed + simplified in the browser — only the polyline is sent),
// rename/recolour existing routes, delete. Every endpoint responds with the
// room's full gpxRoutes array, handed back through onSaved.

const ColorSwatches = ({ value, onChange }) => (
  <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
    {GPX_ROUTE_COLORS.map((option) => (
      <Tooltip key={option} title={option}>
        <Box
          onClick={() => onChange(option)}
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            backgroundColor: option,
            cursor: 'pointer',
            border:
              value === option ? '3px solid #0F172A' : '3px solid transparent',
            boxShadow:
              value === option
                ? '0 0 0 2px white inset'
                : '0 1px 4px rgba(0,0,0,0.25)',
          }}
        />
      </Tooltip>
    ))}
  </Stack>
)

const nextUnusedColor = (routes) =>
  GPX_ROUTE_COLORS.find(
    (color) => !routes.some((route) => route.color === color),
  ) || GPX_ROUTE_COLORS[routes.length % GPX_ROUTE_COLORS.length]

const RouteRow = ({ route, onSaved, onError, controlRoomID }) => {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(route.name)
  const [color, setColor] = useState(route.color || GPX_ROUTE_COLORS[0])
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setName(route.name)
    setColor(route.color || GPX_ROUTE_COLORS[0])
  }, [route.name, route.color])

  const update = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    const res = await API.post(
      `/controlrooms/${controlRoomID}/gpx-routes/${route._id}`,
      { name: trimmed, color },
    )
    setBusy(false)
    if (res.status >= 400) {
      onError(res.data?.error || res.data?.message || 'Could not update the route.')
      return
    }
    onSaved(res.data)
    setEditing(false)
  }

  const remove = async () => {
    setBusy(true)
    const res = await API.post(
      `/controlrooms/${controlRoomID}/gpx-routes/${route._id}/delete`,
    )
    setBusy(false)
    if (res.status >= 400) {
      onError(res.data?.error || res.data?.message || 'Could not delete the route.')
      return
    }
    onSaved(res.data)
  }

  return (
    <Stack gap={1} sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" gap={1.25}>
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: route.color || GPX_ROUTE_COLORS[0],
            flexShrink: 0,
          }}
        />
        {editing ? (
          <TextField
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 60 }}
            autoFocus
            fullWidth
            onKeyDown={(e) => {
              if (e.key === 'Enter') update()
            }}
          />
        ) : (
          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={700} noWrap>
              {route.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatRouteDistance(route.distanceMeters)} ·{' '}
              {route.pointCount ?? route.coordinates?.length ?? 0} points
              {route.sourceFileName ? ` · ${route.sourceFileName}` : ''}
            </Typography>
          </Stack>
        )}
        {editing ? (
          <>
            <IconButton
              size="small"
              color="primary"
              disabled={busy || !name.trim()}
              onClick={update}
            >
              <Check fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={busy}
              onClick={() => {
                setEditing(false)
                setName(route.name)
                setColor(route.color || GPX_ROUTE_COLORS[0])
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </>
        ) : confirmingDelete ? (
          <>
            <Button
              size="small"
              color="error"
              disabled={busy}
              onClick={remove}
            >
              Delete
            </Button>
            <Button
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() => setConfirmingDelete(false)}
            >
              Keep
            </Button>
          </>
        ) : (
          <>
            <IconButton size="small" onClick={() => setEditing(true)}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => setConfirmingDelete(true)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
      {editing && <ColorSwatches value={color} onChange={setColor} />}
    </Stack>
  )
}

const GpxRouteManager = ({
  open,
  onClose,
  controlRoomID,
  gpxRoutes = [],
  onSaved,
}) => {
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(null) // parsed upload awaiting save
  const [pendingName, setPendingName] = useState('')
  const [pendingColor, setPendingColor] = useState(GPX_ROUTE_COLORS[0])
  const [saving, setSaving] = useState(false)
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (!open) return
    setError(null)
    setPending(null)
    setSaving(false)
  }, [open])

  const atCap = gpxRoutes.length >= MAX_GPX_ROUTES

  const pickFile = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setError(null)

    const text = await file.text()
    const parsed = parseGpx(text)
    if (!parsed) {
      setError(
        `Could not read a course from ${file.name} — is it a valid GPX file with a track or route?`,
      )
      return
    }

    const coordinates = simplifyRoute(parsed.coordinates, {
      maxPoints: MAX_GPX_POINTS,
    })
    setPending({
      coordinates,
      originalPointCount: parsed.coordinates.length,
      distanceMeters: routeDistanceMeters(coordinates),
      sourceFileName: file.name,
    })
    setPendingName(parsed.name || file.name.replace(/\.gpx$/i, ''))
    setPendingColor(nextUnusedColor(gpxRoutes))
  }

  const saveNewRoute = async () => {
    const name = pendingName.trim()
    if (!pending || !name) return
    setSaving(true)
    const res = await API.post(`/controlrooms/${controlRoomID}/gpx-routes`, {
      name,
      color: pendingColor,
      coordinates: pending.coordinates,
      sourceFileName: pending.sourceFileName,
    })
    setSaving(false)
    if (res.status >= 400) {
      setError(res.data?.error || res.data?.message || 'Could not save the route.')
      return
    }
    onSaved(res.data)
    setPending(null)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>GPX Routes</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Courses drawn on the GPX map view, with markers every kilometre.
            Upload a .gpx file exported from your course planner.
          </Typography>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {gpxRoutes.length > 0 && (
            <Stack divider={<Divider />} sx={{ mb: 0.5 }}>
              {gpxRoutes.map((route) => (
                <RouteRow
                  key={route._id}
                  route={route}
                  controlRoomID={controlRoomID}
                  onSaved={onSaved}
                  onError={setError}
                />
              ))}
            </Stack>
          )}

          {pending ? (
            <Stack
              gap={1.5}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <Typography fontWeight={700}>New route</Typography>
              <TextField
                label="Name"
                size="small"
                value={pendingName}
                onChange={(e) => setPendingName(e.target.value)}
                inputProps={{ maxLength: 60 }}
                autoFocus
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveNewRoute()
                }}
              />
              <ColorSwatches value={pendingColor} onChange={setPendingColor} />
              <Typography variant="caption" color="text.secondary">
                {formatRouteDistance(pending.distanceMeters)} ·{' '}
                {pending.coordinates.length} points
                {pending.originalPointCount > pending.coordinates.length
                  ? ` (simplified from ${pending.originalPointCount})`
                  : ''}{' '}
                · {pending.sourceFileName}
              </Typography>
              <Stack direction="row" gap={1} justifyContent="flex-end">
                <Button
                  color="inherit"
                  disabled={saving}
                  onClick={() => setPending(null)}
                >
                  Discard
                </Button>
                <Button
                  variant="contained"
                  disabled={saving || !pendingName.trim()}
                  onClick={saveNewRoute}
                >
                  Save Route
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".gpx,application/gpx+xml"
                hidden
                onChange={pickFile}
              />
              <Tooltip
                title={
                  atCap
                    ? `Control rooms can hold up to ${MAX_GPX_ROUTES} GPX routes.`
                    : ''
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<UploadFile />}
                    disabled={atCap}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Add GPX Route
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GpxRouteManager
